import { SkipsProcessingJob } from './SkipsProcessingJob';

interface SkipEventValidationResult {
  isValid: boolean;
  errors: {
    archived: string[];
    continuation: string[];
  };
}

/**
 * Domain entity representing a skip event, which contains either:
 * - Both archived and continuation jobs (most common case)
 * - Only one job (when archiving wasn't needed)
 */
export class SkipEvent {
  public readonly archivedJob: SkipsProcessingJob | null;
  public readonly continuationJob: SkipsProcessingJob | null;

  constructor(archivedJob: SkipsProcessingJob | null, continuationJob: SkipsProcessingJob | null) {
    this.archivedJob = archivedJob;
    this.continuationJob = continuationJob;

    if (!archivedJob && !continuationJob) {
      throw new Error('SkipEvent must have at least one job');
    }
  }

  get hasBothJobs(): boolean {
    return this.archivedJob !== null && this.continuationJob !== null;
  }

  getId(): string {
    const parts: string[] = [];

    if (this.archivedJob) {
      parts.push(this.archivedJob.id);
    }

    if (this.continuationJob) {
      parts.push(this.continuationJob.id);
    }

    return parts.join('-');
  }

  validate(): SkipEventValidationResult {
    const archivedErrors: string[] = [];
    const continuationErrors: string[] = [];

    if (this.archivedJob) {
      const archivedValidation = this.archivedJob.validate();
      archivedErrors.push(...archivedValidation.errors);
    }

    if (this.continuationJob) {
      const continuationValidation = this.continuationJob.validate();
      continuationErrors.push(...continuationValidation.errors);
    }

    return {
      isValid: archivedErrors.length === 0 && continuationErrors.length === 0,
      errors: {
        archived: archivedErrors,
        continuation: continuationErrors,
      },
    };
  }

  hasChanges(): boolean {
    let hasChanges = false;

    if (this.archivedJob) {
      hasChanges = hasChanges || this.archivedJob.hasChanges();
    }

    if (this.continuationJob) {
      hasChanges = hasChanges || this.continuationJob.hasChanges();
    }

    return hasChanges;
  }

  copyShiftToArchived(): void {
    if (this.archivedJob && this.continuationJob) {
      this.archivedJob.setShift(this.continuationJob.shift);
    }
  }
}
